import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { Switch, Redirect, useParams } from "react-router-dom";
import * as Screens from "src/screens";
import { PublicRoute } from "src/components";
import { Realestate } from "src/screens/process/realestate/Realestate";
import { Contact } from "src/screens/process/contact/Contact";
import { ContactPersons } from "src/screens/process/contactpersons/ContactPersons";
import { Nameplates } from "src/screens/process/nameplates/Nameplates";
import { Keys } from "src/screens/process/keys/Keys";
import { DocumentQuestion } from "src/screens/process/documentquestion/DocumentQuestion";
import { Finish } from "src/screens/process/finish/Finish";
import { SelectComponentList } from "./step/SelectComponentList";
import { Damage } from "src/screens/process/damage/Damage";
import { Text } from "src/screens/process/text/Text";
import { Select } from "./step/Select";
import { observer } from "mobx-react";
import { STEP_TYPES } from "src/api/Processes";
import { runInAction } from "mobx";
import { getRoute, Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { getLang } from "src/utils/i18n";

const ProcessBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, navStore } = useContext(RootStoreContext);
    const { processId } = useParams<{ processId: string; stepId: string }>();

    const process = easyContactStore.getProcessById(processId);
    const firstStep = process?.steps[0];

    useEffect(() => {
        easyContactStore.setNotificationtype(process?.notificationtype ?? 0);

        runInAction(() => {
            if (process?.process_mls) {
                const title = process?.process_mls.find((process) => process.language === getLang())?.title;
                navStore.setTitle(title ?? "");
            }
        });

        return () => {
            runInAction(() => {
                navStore.reset();
            });
        };
    }, [easyContactStore, navStore, process, easyContactStore.applicationLanguage]);

    const wasLocalStorageInitialized = localStorage.getItem("@easyContactStore");
    if (!wasLocalStorageInitialized) {
        return <Redirect to={Route.start} />;
    }

    if (!process || !firstStep) {
        return <Screens.Error message={t("error.invalid-data")} />;
    }

    return (
        <div className="d-flex flex-grow-1 flex-column pb-4">
            <Switch>
                <PublicRoute path={getRoute(Route.select, { stepType: STEP_TYPES.SELECT })} component={Select} exact />
                <PublicRoute
                    path={getRoute(Route.selectComponent, { stepType: STEP_TYPES.SELECT })}
                    component={SelectComponentList}
                    exact
                />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.DAMAGE })} component={Damage} />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.TEXT })} component={Text} />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.NAMEPLATES })} component={Nameplates} />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.KEYS })} component={Keys} />
                <PublicRoute
                    path={getRoute(Route.step, { stepType: STEP_TYPES.DOCUMENT_QUESTION })}
                    component={DocumentQuestion}
                />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.REALESTATE })} component={Realestate} />
                <PublicRoute
                    path={getRoute(Route.step, { stepType: STEP_TYPES.CONTACT_PERSONS })}
                    component={ContactPersons}
                />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.CONTACT })} component={Contact} />
                <PublicRoute path={getRoute(Route.step, { stepType: STEP_TYPES.FINISH })} component={Finish} />
                <Redirect
                    to={selectRoute(Route.step, { processId, stepType: String(firstStep?.type), stepId: firstStep.id })}
                />
            </Switch>
        </div>
    );
};

export const Process = Sentry.withProfiler(observer(ProcessBase));
